import { useCallback, useEffect, useRef, useState } from 'react'
import { Socket, io } from 'socket.io-client'
import { useAuth } from '../../../Contexts/AuthContext'
import { resolveSocketUrl } from '../../../utils/exportDownload'
import { notificationSound } from '../../../utils/notificationSound'
import { Notification, NotificationResponse, NotificationStats } from './types'

type UseNotificationsOptions = {
  userId: number
  apiEndpoint: string
  fetchNotifications: (
    userId: number,
    filters?: Record<string, unknown>,
  ) => Promise<NotificationResponse>
  fetchStats: (userId: number) => Promise<NotificationStats>
  markAsRead: (id: number) => Promise<void>
  markAllAsRead: (userId: number) => Promise<void>
  dismissNotification: (id: number) => Promise<void>
  dismissAllNotifications: (userId: number) => Promise<void>
  notificationType?: string
  /** Stable array reference recommended (module-level const). */
  notificationTypes?: readonly string[]
  onNewNotification?: (notification: Notification) => void
}

function typesFilterKey(
  notificationType?: string,
  notificationTypes?: readonly string[],
): string {
  if (notificationTypes?.length) {
    return notificationTypes.slice().sort().join('|')
  }
  return notificationType ?? ''
}

/**
 * Generic hook for managing notifications.
 */
export const useNotifications = ({
  userId,
  fetchNotifications,
  fetchStats,
  markAsRead,
  markAllAsRead,
  dismissNotification,
  dismissAllNotifications,
  notificationType,
  notificationTypes,
  onNewNotification,
}: UseNotificationsOptions) => {
  const typesKey = typesFilterKey(notificationType, notificationTypes)
  const matchesTypeRef = useRef<(type: string) => boolean>(() => true)
  matchesTypeRef.current = (type: string) => {
    if (notificationTypes?.length) return notificationTypes.includes(type)
    if (notificationType) return type === notificationType
    return true
  }

  const onNewNotificationRef = useRef(onNewNotification)
  onNewNotificationRef.current = onNewNotification

  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [stats, setStats] = useState<NotificationStats>({
    total: 0,
    unread: 0,
    read: 0,
    dismissed: 0,
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasNewNotification, setHasNewNotification] = useState(false)
  const socketRef = useRef<Socket | null>(null)
  const fetchInFlightRef = useRef(false)

  const applyFilteredNotifications = useCallback(
    (list: Notification[]) => {
      const filtered = list.filter(
        n => matchesTypeRef.current(n.type) && !n.isDeleted,
      )
      const unread = filtered.filter(n => n.status === 'unread').length
      setNotifications(filtered)
      setStats({
        total: filtered.length,
        unread,
        read: filtered.length - unread,
        dismissed: 0,
      })
      return filtered
    },
    [],
  )

  const refreshNotifications = useCallback(async () => {
    if (!userId || fetchInFlightRef.current) return

    fetchInFlightRef.current = true
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchNotifications(userId, {
        limit: 50,
        includeDeleted: false,
      })
      applyFilteredNotifications(response.notifications)
    } catch (err) {
      console.error('Error fetching notifications:', err)
      setError('Erreur lors du chargement des notifications')
    } finally {
      setIsLoading(false)
      fetchInFlightRef.current = false
    }
  }, [userId, fetchNotifications, applyFilteredNotifications])

  const refreshStats = useCallback(async () => {
    if (!userId || typesKey) return

    try {
      const statsData = await fetchStats(userId)
      setStats(statsData)
    } catch (err) {
      console.error('Error fetching notification stats:', err)
    }
  }, [userId, fetchStats, typesKey])

  const handleMarkAsRead = useCallback(
    async (notificationId: number) => {
      try {
        await markAsRead(notificationId)

        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, status: 'read' as const }
              : notification,
          ),
        )

        setStats(prev => ({
          ...prev,
          unread: Math.max(0, prev.unread - 1),
          read: prev.read + 1,
        }))
      } catch (err) {
        console.error('Error marking notification as read:', err)
        setError('Erreur lors de la mise à jour de la notification')
      }
    },
    [markAsRead],
  )

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await markAllAsRead(userId)

      setNotifications(prev =>
        prev.map(notification => ({
          ...notification,
          status: 'read' as const,
        })),
      )

      setStats(prev => ({
        ...prev,
        unread: 0,
        read: prev.total,
      }))
    } catch (err) {
      console.error('Error marking all notifications as read:', err)
      setError('Erreur lors de la mise à jour des notifications')
    }
  }, [userId, markAllAsRead])

  const handleDismissNotification = useCallback(
    async (notificationId: number) => {
      try {
        await dismissNotification(notificationId)

        setNotifications(prev =>
          prev.filter(notification => notification.id !== notificationId),
        )

        setStats(prev => ({
          ...prev,
          total: prev.total - 1,
          unread: Math.max(0, prev.unread - 1),
          dismissed: prev.dismissed + 1,
        }))
      } catch (err) {
        console.error('Error dismissing notification:', err)
        setError('Erreur lors de la suppression de la notification')
      }
    },
    [dismissNotification],
  )

  const handleDismissAllNotifications = useCallback(async () => {
    try {
      await dismissAllNotifications(userId)

      setNotifications([])
      setStats(prev => ({
        ...prev,
        total: 0,
        unread: 0,
        read: 0,
        dismissed: prev.dismissed + prev.total,
      }))
    } catch (err) {
      console.error('Error dismissing all notifications:', err)
      setError('Erreur lors de la suppression des notifications')
    }
  }, [userId, dismissAllNotifications])

  const refresh = useCallback(() => {
    void refreshNotifications()
    if (!typesKey) void refreshStats()
  }, [refreshNotifications, refreshStats, typesKey])

  useEffect(() => {
    if (!userId) return
    void refreshNotifications()
    if (!typesKey) void refreshStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on user + filter only
  }, [userId, typesKey])

  useEffect(() => {
    if (!userId || !user) return

    const socket = io(resolveSocketUrl(), {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    })
    socketRef.current = socket

    socket.on('new_notification', (notification: Notification) => {
      if (!matchesTypeRef.current(notification.type) || notification.isDeleted) {
        return
      }

      setNotifications(prev => {
        if (prev.some(n => n.id === notification.id)) return prev
        return [notification, ...prev]
      })

      setStats(prev => ({
        ...prev,
        total: prev.total + 1,
        unread: prev.unread + 1,
      }))

      setHasNewNotification(true)
      notificationSound.play()
      onNewNotificationRef.current?.(notification)
    })

    socket.on('notification_updated', (updatedNotification: Notification) => {
      if (!matchesTypeRef.current(updatedNotification.type)) return

      setNotifications(prev => {
        if (updatedNotification.isDeleted) {
          const removed = prev.find(n => n.id === updatedNotification.id)
          setStats(s => ({
            ...s,
            total: Math.max(0, s.total - 1),
            unread:
              removed?.status === 'unread'
                ? Math.max(0, s.unread - 1)
                : s.unread,
          }))
          return prev.filter(n => n.id !== updatedNotification.id)
        }

        return prev.map(n =>
          n.id === updatedNotification.id ? updatedNotification : n,
        )
      })
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
    }
  }, [userId, user, typesKey])

  const resetAnimation = useCallback(() => {
    setHasNewNotification(false)
  }, [])

  return {
    notifications,
    stats,
    isLoading,
    error,
    hasNewNotification,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    dismissNotification: handleDismissNotification,
    dismissAllNotifications: handleDismissAllNotifications,
    refresh,
    resetAnimation,
  }
}
